// xmp_metadata:
// http://ns.google.com/photos/1.0/panorama/:
// GPano:CroppedAreaImageHeightPixels: 640
// GPano:CroppedAreaImageWidthPixels: 960
// GPano:CroppedAreaLeftPixels: 1187
// GPano:CroppedAreaTopPixels: 514
// GPano:FullPanoHeightPixels: 1667
// GPano:FullPanoWidthPixels: 3334
// GPano:InitialViewHeadingDegrees: 0
// GPano:ProjectionType: "equirectangular"
// GPano:UsePanoramaViewer: true

import type {IPhotoSphereMetaData} from "../../ui/modules/space/spaceeditor/logic3d/elements3d/markups/MarkupPhoto360Utils";
import {FileUtils} from "../../utils/file/FileUtils";
import {MathUtils} from "../../utils/math/MathUtils";
import type {PhotoSphere} from "../PhotoSphere";
import {PhotoSphereShader} from "../PhotoSphereShader";

const XMPMetaDataKeys: (keyof IXMPMetaData)[] = [
	"GPano:CroppedAreaImageHeightPixels",
	"GPano:CroppedAreaImageWidthPixels",
	"GPano:CroppedAreaLeftPixels",
	"GPano:CroppedAreaTopPixels",
	"GPano:FullPanoHeightPixels",
	"GPano:FullPanoWidthPixels",
	"GPano:InitialViewHeadingDegrees",
];

interface IXMPMetaData {
	"GPano:CroppedAreaImageHeightPixels"?: number;
	"GPano:CroppedAreaImageWidthPixels"?: number;
	"GPano:CroppedAreaLeftPixels"?: number;
	"GPano:CroppedAreaTopPixels"?: number;
	"GPano:FullPanoHeightPixels"?: number;
	"GPano:FullPanoWidthPixels"?: number;
	"GPano:InitialViewHeadingDegrees"?: number;
}

export class ImageUtils {
	private static _canvas: HTMLCanvasElement = document.createElement("canvas");
	private static _ctx: CanvasRenderingContext2D = ImageUtils._canvas.getContext("2d");
	private static _cache: {[id: string]: Promise<HTMLImageElement>} = {};

	public static getImage(url: string, maxTextureSize: number): Promise<Exclude<TexImageSource, VideoFrame>> {
		return new Promise<HTMLImageElement>(async (resolve, reject) => {
			const originalImage = await ImageUtils.getImageElement(url);
			const largestSize = Math.max(originalImage.width, originalImage.height);

			if (maxTextureSize < largestSize) {
				// is resize needed
				const textureSize = Math.min(largestSize, maxTextureSize);
				const ratio = originalImage.width / originalImage.height;
				const newSize = {width: 1, height: 1};

				if (ratio >= 1) {
					// width > height
					newSize.width = textureSize;
					newSize.height = Math.min(textureSize, originalImage.height);
				} else {
					newSize.height = textureSize;
					newSize.width = Math.min(textureSize, originalImage.width);
				}

				ImageUtils._canvas.width = newSize.width;
				ImageUtils._canvas.height = newSize.height;
				ImageUtils._ctx.drawImage(originalImage, 0, 0, newSize.width, newSize.height);
				ImageUtils._canvas.toBlob(async (blob: Blob) => {
					const resizedImage = await ImageUtils.getImageElement(URL.createObjectURL(blob));
					resolve(resizedImage);
				});
			} else {
				resolve(originalImage);
			}
		});
	}

	/**
	 * Same as getImageElement, only it's not using cache. Needed if we use the same image in multiple places (bottom row thumbnails + hotspot thumbnails for example)
	 */
	public static getImageElement(url: string) {
		return new Promise<HTMLImageElement>((resolve, reject) => {
			const img = document.createElement("img");
			img.setAttribute("crossorigin", "anonymous");
			img.onload = () => {
				resolve(img);
			};
			img.onerror = () => {
				reject(img);
			};
			img.src = url;
		});
	}

	private static getCachedImageElement(url: string) {
		if (!ImageUtils._cache[url]) {
			ImageUtils._cache[url] = this.getImageElement(url);
		}

		return ImageUtils._cache[url];
	}

	private static async getXMPMetaDataFromImageFile(file: File) {
		const fileAsText = await FileUtils.readAsText(file);

		const xmpMetaData: IXMPMetaData = {};
		for (const key of XMPMetaDataKeys) {
			xmpMetaData[key] = parseFloat(FileUtils.getValueFromTextByKey(fileAsText, key));
		}

		return xmpMetaData;
	}

	public static async getMetaDataFromFile(file: File): Promise<IPhotoSphereMetaData> {
		// UV space
		const viewBox = PhotoSphereShader.defaultViewBox;
		const imageUrl = URL.createObjectURL(file);
		const image = await this.getCachedImageElement(imageUrl);
		const metadata = await this.getXMPMetaDataFromImageFile(file);

		// There might be a case when the heading angle is set, but nothing else is present within this object, so we need to check another property
		if (!metadata["GPano:FullPanoWidthPixels"]) {
			const differenceY = image.width / 2 - image.height;
			const fullHeight = image.height + differenceY;
			if (differenceY > 1) {
				const offsetY = differenceY / 2;
				viewBox[1] = offsetY / fullHeight;
				viewBox[3] = (fullHeight - offsetY) / fullHeight;
			}
		} else {
			const fullPanoWidth = metadata["GPano:FullPanoWidthPixels"];
			const fullPanoHeight = metadata["GPano:FullPanoHeightPixels"];
			const cropLeft = metadata["GPano:CroppedAreaLeftPixels"];
			const cropTop = metadata["GPano:CroppedAreaTopPixels"];
			const originalImageWidth = metadata["GPano:CroppedAreaImageWidthPixels"];
			const originalImageHeight = metadata["GPano:CroppedAreaImageHeightPixels"];

			if (fullPanoWidth !== originalImageWidth || fullPanoHeight !== originalImageHeight) {
				viewBox[0] = cropLeft / fullPanoWidth;
				viewBox[1] = (fullPanoHeight - originalImageHeight - cropTop) / fullPanoHeight;
				viewBox[2] = (cropLeft + originalImageWidth) / fullPanoWidth;
				viewBox[3] = (fullPanoHeight - cropTop) / fullPanoHeight;
			}
		}

		const headingAngleInDegrees = (metadata["GPano:InitialViewHeadingDegrees"] + 180) % 360;

		const headingAngle = MathUtils.isValidNumber(headingAngleInDegrees) ? MathUtils.DEG2RAD * headingAngleInDegrees : 0;

		return {
			viewBox: viewBox,
			headingAngle: headingAngle,
			originalWidth: image.width,
			originalHeight: image.height,
		};
	}

	public static async getImageForTexture(url: string, photoSphere: PhotoSphere, maxTextureSize: number) {
		const textureData = await ImageUtils.getImage(url, maxTextureSize);

		return {
			textureData: textureData,
			viewBox: photoSphere.viewBox,
			photoSphere: photoSphere,
		};
	}
}
